import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../stores/Context';

/**
 * WorkspaceSelector — Phase 2 SEIZO
 *
 * Sélecteur de workspace affiché dans la barre de navigation de la page Totem.
 * Charge la liste des workspaces au montage et permet de switcher entre eux.
 *
 * Comportement :
 *   - 0 workspace  : masqué (rien à afficher)
 *   - 1+ workspace : dropdown avec le workspace actif affiché + option "Tous les écrans"
 *                    (important : les totems sans workspace_id ne sont visibles qu'avec "Tous les écrans")
 *
 * Props :
 *   ent (string|undefined) — code de l'entreprise (conservé pour compatibilité, peut être undefined en Phase 2)
 *   onWorkspaceChange (function) — callback appelé quand le workspace change
 */
const WorkspaceSelector = observer(({ ent, onWorkspaceChange }) => {
    const { workspaceStore } = useStores();
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Charger les workspaces au montage — ne dépend plus de ent (Phase 2 : URL sans code espace)
    useEffect(() => {
        if (workspaceStore.workspaces.length === 0 && !workspaceStore.loading) {
            workspaceStore.loadWorkspaces(ent); // ent peut être undefined, le store utilise current_apiToken
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Notifier le parent quand le workspace actif change
    useEffect(() => {
        if (onWorkspaceChange) {
            onWorkspaceChange(workspaceStore.activeWorkspace);
        }
    }, [workspaceStore.activeWorkspace]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fermer le dropdown en cliquant en dehors
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (workspace) => {
        workspaceStore.setActiveWorkspace(ent, workspace);
        setOpen(false);
    };

    // Pendant le chargement : rien (évite le flash furtif)
    if (workspaceStore.loading) {
        return null;
    }

    // Aucun workspace : rien à afficher
    if (!workspaceStore.workspaces || workspaceStore.workspaces.length === 0) {
        return null;
    }

    // 1 workspace ou plus : toujours afficher le dropdown
    // (les totems sans workspace_id ne sont accessibles que via "Tous les écrans")
    const activeLabel = workspaceStore.activeWorkspace
        ? workspaceStore.activeWorkspace.name
        : 'Tous les écrans';

    return (
        <div ref={dropdownRef} style={styles.container}>
            <button
                style={styles.trigger}
                onClick={() => setOpen(!open)}
                title="Changer de workspace"
            >
                <span style={styles.icon}>⬡</span>
                <span style={styles.label}>{activeLabel}</span>
                <span style={{ ...styles.arrow, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
            </button>

            {open && (
                <div style={styles.dropdown}>
                    <div style={styles.dropdownHeader}>Workspaces</div>
                    {workspaceStore.workspaces.map((ws) => (
                        <button
                            key={ws.id}
                            style={{
                                ...styles.dropdownItem,
                                ...(workspaceStore.activeWorkspace?.id === ws.id
                                    ? styles.dropdownItemActive
                                    : {}),
                            }}
                            onClick={() => handleSelect(ws)}
                        >
                            <span style={styles.wsName}>{ws.name}</span>
                            {ws.totems_count !== undefined && (
                                <span style={styles.wsBadge}>{ws.totems_count} écran{ws.totems_count > 1 ? 's' : ''}</span>
                            )}
                        </button>
                    ))}
                    <button
                        style={{
                            ...styles.dropdownItemAll,
                            ...(workspaceStore.activeWorkspace === null
                                ? styles.dropdownItemAllActive
                                : {}),
                        }}
                        onClick={() => handleSelect(null)}
                    >
                        Tous les écrans
                    </button>
                </div>
            )}
        </div>
    );
});

const styles = {
    container: {
        position: 'relative',
        display: 'inline-block',
    },
    trigger: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.4rem 0.75rem',
        background: 'rgba(0,35,53,0.06)',
        border: '1px solid rgba(0,35,53,0.15)',
        borderRadius: '0.5rem',
        color: '#002335',
        fontSize: '0.9rem',
        fontFamily: 'Nunito, sans-serif',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
    icon: {
        fontSize: '1rem',
        color: '#00B0FF',
    },
    label: {
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '0.85rem',
    },
    arrow: {
        fontSize: '0.75rem',
        transition: 'transform 0.2s',
    },
    dropdown: {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        minWidth: '200px',
        background: '#fff',
        border: '1px solid #DBE3E7',
        borderRadius: '0.5rem',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        zIndex: 9999,
        overflow: 'hidden',
    },
    dropdownHeader: {
        padding: '0.5rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#8A9BAB',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #F0F4F6',
    },
    dropdownItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '0.6rem 0.75rem',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.9rem',
        color: '#002335',
        fontFamily: 'Nunito, sans-serif',
        textAlign: 'left',
        transition: 'background 0.15s',
    },
    dropdownItemActive: {
        background: '#EAF6FF',
        fontWeight: 700,
        color: '#00B0FF',
    },
    dropdownItemAll: {
        display: 'block',
        width: '100%',
        padding: '0.6rem 0.75rem',
        background: 'transparent',
        border: 'none',
        borderTop: '1px solid #F0F4F6',
        cursor: 'pointer',
        fontSize: '0.85rem',
        color: '#8A9BAB',
        fontFamily: 'Nunito, sans-serif',
        textAlign: 'left',
        fontStyle: 'italic',
    },
    dropdownItemAllActive: {
        background: '#EAF6FF',
        fontWeight: 700,
        color: '#00B0FF',
        fontStyle: 'normal',
    },
    wsName: {
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    wsBadge: {
        marginLeft: '0.5rem',
        padding: '0.1rem 0.4rem',
        background: '#F0F4F6',
        borderRadius: '0.75rem',
        fontSize: '0.75rem',
        color: '#8A9BAB',
        whiteSpace: 'nowrap',
    },
};

export default WorkspaceSelector;
